import React from 'react';
import { useDispatch } from "react-redux";
import { setPageTitle, setStatusBarMessage } from '../../app/actions';
import axios from "axios";
import { getJwtToken } from '../Common/Auth';
import { getApiURL } from '../Common/envVarsReader';
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Select from "@amzn/awsui-components-react/polaris/select";
import Input from "@amzn/awsui-components-react/polaris/input";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Button from "@amzn/awsui-components-react/polaris/button";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Box from "@amzn/awsui-components-react/polaris/box";

// ===== EMAIL TEMPLATES =====
const EMAIL_TEMPLATES = {
    severity_review: {
        label: 'Critical Severity CVE Review',
        description: 'Request BST review of critical severity CVEs before filing',
        generateSubject: (vars) => {
            const month = vars.month || '<Month>';
            const year = vars.year || '<Year>';
            const count = vars.cveCount || 'N';
            return `[ASB] Action Required: ${count} Critical Severity CVEs - ${month} ${year} ASB Review`;
        },
        generateBody: (vars) => {
            const month = vars.month || '<Month>';
            const year = vars.year || '<Year>';
            const totalCritical = vars.cveCount || 'N';
            const priorityCount = vars.priorityCount || 'N';
            const cveTable = vars.cveTable || `CVE\tSEVERITY\tCOMPONENT\tTYPE\tDESCRIPTION
CVE-YYYY-NNNNN\tCritical\t<Component>\t<Type>\t<Description>`;
            return `Hi BST Team,

As part of the upcoming ${month} ${year} Android Security Bulletin (ASB) intake, ${totalCritical} CVEs were flagged as Critical severity. We completed our preliminary review and identified ${priorityCount} CVEs with Remote Code Execution (RCE) or Escalation of Privilege (EoP) impact that require your attention before we proceed with ticket filing.

Please review and confirm the severity assessment for the ${priorityCount} priority CVEs listed below within 5 business days. If we do not receive confirmation by this date, we will proceed with filing these as High severity per our standard ASB process.

${cveTable}

Full list of all ${totalCritical} Critical CVEs is attached as CSV for reference.

Thanks,

Suhyun Smith
SBS EVO Team
sbs-security-bulletins@amazon.com`;
        }
    },
    firetv_metrics: {
        label: 'FireTV ASB Jiras Notification',
        description: 'Notify FireTV team of filed ASB Jiras with metrics CSV',
        generateSubject: (vars) => {
            const month = vars.month || '<Month>';
            const year = vars.year || '<Year>';
            return `FireTV ASB Jiras for ${month} ${year}`;
        },
        generateBody: (vars) => {
            const month = vars.month || '<Month>';
            return `Hi Claudia and Shaun,

Please find attached the FireTV ASB Jiras we filed for ${month}.

Thanks!

Suhyun Smith
SBS EVO Team
sbs-security-bulletins@amazon.com`;
        }
    }
};

function SendButton(props) {
    if (props.loading) {
        return <Spinner />;
    }
    if (props.disabled) {
        return (<Button disabled variant="primary">Send Email</Button>);
    }
    return (<Button onClick={(e) => { e.preventDefault(); props.sendEmail(); }} variant="primary">Send Email</Button>);
}

export default function EmailNotificationPage() {
    const dispatch = useDispatch();
    const [loading, setLoading] = React.useState(false);
    const [template, setTemplate] = React.useState(null);
    const [asbMonth, setAsbMonth] = React.useState(null);
    const [toRecipients, setToRecipients] = React.useState('');
    const [ccRecipients, setCcRecipients] = React.useState('');
    const [subject, setSubject] = React.useState('');
    const [body, setBody] = React.useState('');
    const [sent, setSent] = React.useState(false);

    React.useEffect(() => {
        dispatch(setPageTitle('Email Notification'));
    }, []);

    const templateOptions = [
        { label: EMAIL_TEMPLATES.severity_review.label, value: 'severity_review', description: EMAIL_TEMPLATES.severity_review.description },
        { label: EMAIL_TEMPLATES.firetv_metrics.label, value: 'firetv_metrics', description: EMAIL_TEMPLATES.firetv_metrics.description },
    ];

    // Generate ASB month options (current month + last 3 months)
    const getAsbMonthOptions = () => {
        const options = [];
        const now = new Date();
        for (let i = 0; i < 4; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];
            const monthName = monthNames[d.getMonth()];
            const year = d.getFullYear();
            options.push({
                label: `${monthName} ${year}`,
                value: `${monthName}|${year}`,
                description: `${year}-${String(d.getMonth() + 1).padStart(2, '0')} ASB`
            });
        }
        return options;
    };

    const asbMonthOptions = getAsbMonthOptions();

    const onTemplateChange = (selectedOption) => {
        setTemplate(selectedOption);
        setSent(false);
        // Auto-fill recipients based on template
        if (selectedOption.value === 'severity_review') {
            setToRecipients('');
            setCcRecipients('');
        } else if (selectedOption.value === 'firetv_metrics') {
            setToRecipients('claudjm@amazon.com');
            setCcRecipients('shaunwxx@amazon.com, samxu@amazon.com, jrhao@amazon.com');
        }
        // Generate with current ASB month if already selected
        if (asbMonth) {
            generateFromTemplate(selectedOption.value, asbMonth.value);
        }
    };

    const onAsbMonthChange = (selectedOption) => {
        setAsbMonth(selectedOption);
        setSent(false);
        // Regenerate template if one is already selected
        if (template) {
            generateFromTemplate(template.value, selectedOption.value);
        }
    };

    const generateFromTemplate = (templateKey, monthValue) => {
        if (!EMAIL_TEMPLATES[templateKey] || !monthValue) return;

        const [month, year] = monthValue.split('|');
        const vars = { month, year };

        setSubject(EMAIL_TEMPLATES[templateKey].generateSubject(vars));
        setBody(EMAIL_TEMPLATES[templateKey].generateBody(vars));
    };

    const sendEmail = async () => {
        setLoading(true);
        dispatch(setStatusBarMessage(null));
        try {
            const options = {
                headers: {
                    'Authorization': `Bearer ${getJwtToken()}`,
                    'Content-Type': 'application/json'
                },
            };
            const data = {
                to: toRecipients.split(',').map(e => e.trim()).filter(e => e),
                cc: ccRecipients.split(',').map(e => e.trim()).filter(e => e),
                subject: subject,
                body: body,
                template: template ? template.value : 'custom'
            };
            let response = await axios.post(`${getApiURL()}/notifications/email`, data, options);
            dispatch(setStatusBarMessage({ type: 'success', message: 'Email sent successfully!' }));
            setSent(true);
        } catch (e) {
            console.log(e);
            dispatch(setStatusBarMessage({ type: 'error', message: 'Failed to send email: ' + e }));
        }
        setLoading(false);
    };

    return (
        <SpaceBetween size='l'>
            <Container
                header={
                    <Header variant="h2" description="Send an email notification for ASB updates.">
                        Email Notification
                    </Header>
                }
            >
                <SpaceBetween size="l">
                    <FormField label="Template" description="Select an email template to auto-populate fields.">
                        <Select
                            selectedOption={template}
                            onChange={({ detail }) => onTemplateChange(detail.selectedOption)}
                            options={templateOptions}
                            placeholder="Choose a template"
                        />
                    </FormField>
                    <FormField label="ASB Month" description="Select which ASB month this notification references.">
                        <Select
                            selectedOption={asbMonth}
                            onChange={({ detail }) => onAsbMonthChange(detail.selectedOption)}
                            options={asbMonthOptions}
                            placeholder="Choose ASB month"
                        />
                    </FormField>
                    {template && asbMonth && (
                        <Box variant="awsui-key-label" color="text-status-info">
                            Template: {template.label} — {asbMonth.label}. Edit fields below before sending.
                        </Box>
                    )}
                    <FormField label="To" description="Comma-separated email addresses.">
                        <Input
                            onChange={({ detail }) => setToRecipients(detail.value)}
                            value={toRecipients}
                            placeholder="recipient@amazon.com"
                        />
                    </FormField>
                    <FormField label="CC" description="Optional CC recipients (comma-separated).">
                        <Input
                            onChange={({ detail }) => setCcRecipients(detail.value)}
                            value={ccRecipients}
                            placeholder="cc@amazon.com"
                        />
                    </FormField>
                    <FormField label="Subject">
                        <Input
                            onChange={({ detail }) => setSubject(detail.value)}
                            value={subject}
                            placeholder="[ASB] Subject line"
                        />
                    </FormField>
                    <FormField label="Body">
                        <Textarea
                            onChange={({ detail }) => setBody(detail.value)}
                            value={body}
                            rows={18}
                            placeholder="Select a template and ASB month above to auto-populate, or type a custom email."
                        />
                    </FormField>
                </SpaceBetween>
            </Container>
            <div align="right">
                <SendButton
                    loading={loading}
                    disabled={!toRecipients || !subject || !body || sent}
                    sendEmail={sendEmail}
                />
            </div>
        </SpaceBetween>
    );
}
